import { useEffect, useState } from "react";
import { kaggleTierLabels } from "../data/kaggleTiers";
import { formatAcademicGrade } from "../data/academicGrades";
import {
  mockActiveCompetitions,
  mockCompletedCompetitions,
  type ActiveCompetition,
} from "../data/mockActiveCompetitions";
import { getPaginationWindow, Pagination } from "./Pagination";

type CompetitionsResponse = {
  status?: string;
  message?: string;
  competitions?: ActiveCompetition[];
  active_competitions?: ActiveCompetition[];
  completed_competitions?: ActiveCompetition[];
};

type CompetitionsState = "loading" | "ready" | "error";
type CompetitionKind = "active" | "completed";

const competitionPageSize = 20;

function isLocalDevelopment() {
  return ["", "localhost", "127.0.0.1", "::1"].includes(
    window.location.hostname
  );
}

function formatCompetitionEnd(value: string) {
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const normalized = value.replace(" ", "T") + (hasTimezone ? "" : "+09:00");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatParticipantRank(aiyssRank: number, overallRank: number) {
  return `${aiyssRank}位（${overallRank.toLocaleString("ja-JP")}位）`;
}

function CompetitionGroup({
  kind,
  state,
  competitions,
  currentPage,
  onPageChange,
}: {
  kind: CompetitionKind;
  state: CompetitionsState;
  competitions: ActiveCompetition[];
  currentPage: number;
  onPageChange: (page: number) => void;
}) {
  const isActive = kind === "active";
  const title = isActive ? "開催中のコンペ" : "終了済みのコンペ";
  const headingId = `${kind}-competitions-title`;
  const pagination = getPaginationWindow(
    competitions.length,
    currentPage,
    competitionPageSize
  );
  const visibleCompetitions = competitions.slice(
    pagination.startIndex,
    pagination.endIndex
  );

  return (
    <section
      className={`active-competition-group is-${kind}`}
      aria-labelledby={headingId}
    >
      <div className="active-competitions-heading">
        <div>
          <p className="league-eyebrow">
            {isActive ? "ACTIVE COMPETITIONS" : "COMPLETED COMPETITIONS"}
          </p>
          <h3 id={headingId}>{title}</h3>
        </div>
        <p>1,000チーム以上 / AIYSS登録者内順位（Kaggle全体順位）</p>
      </div>

      {state === "loading" && (
        <p className="active-competitions-message">
          {title}を読み込んでいます…
        </p>
      )}

      {state === "error" && (
        <p className="active-competitions-message is-error">
          {title}を取得できませんでした。
        </p>
      )}

      {state === "ready" && competitions.length === 0 && (
        <p className="active-competitions-message">
          AIYSS登録者の順位が確定している1,000チーム以上の
          {title}はありません。
        </p>
      )}

      {state === "ready" && competitions.length > 0 && (
        <>
          <div className="active-competition-list">
            {visibleCompetitions.map((competition) => (
              <details
                className="active-competition-card"
                key={competition.competition_key}
              >
                <summary className="active-competition-card-header">
                  <div>
                    <span>
                      {isActive ? "コンペ終了予定日" : "コンペ終了日"}{" "}
                      {formatCompetitionEnd(competition.ends_at)}
                    </span>
                    <strong className="active-competition-card-title">
                      {competition.url ? (
                        <a
                          href={competition.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {competition.title}
                          <span aria-hidden="true"> ↗</span>
                        </a>
                      ) : (
                        competition.title
                      )}
                    </strong>
                  </div>
                  <div className="active-competition-card-side">
                    <p>
                      {competition.registered_participant_count} AIYSS
                      PARTICIPANTS
                      {competition.participant_count !== null && (
                        <small>
                          Kaggle{" "}
                          {competition.participant_count.toLocaleString("ja-JP")}
                          チーム
                        </small>
                      )}
                    </p>
                    <span
                      className="active-competition-toggle"
                      aria-hidden="true"
                    />
                  </div>
                </summary>

                <div className="active-competition-table-wrap">
                  <table className="active-competition-table">
                    <thead>
                      <tr>
                        <th scope="col">参加者</th>
                        <th scope="col">AIYSS順位（Kaggle全体順位）</th>
                        <th scope="col">学年</th>
                      </tr>
                    </thead>
                    <tbody>
                      {competition.participants.map((participant) => {
                        const tierLabel = participant.kaggle_tier
                          ? kaggleTierLabels[participant.kaggle_tier]
                          : null;
                        return (
                          <tr key={participant.kaggle_id}>
                            <th scope="row" data-label="参加者">
                              <a
                                className="league-ranking-name"
                                data-kaggle-tier={
                                  participant.kaggle_tier ?? undefined
                                }
                                href={`https://www.kaggle.com/${encodeURIComponent(
                                  participant.kaggle_id
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={tierLabel ?? undefined}
                              >
                                {participant.kaggle_id}
                              </a>
                            </th>
                            <td data-label="順位">
                              {formatParticipantRank(
                                participant.aiyss_rank,
                                participant.overall_rank
                              )}
                            </td>
                            <td data-label="学年">
                              {formatAcademicGrade(participant.grade)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
          <Pagination
            className="active-competition-pagination"
            currentPage={pagination.page}
            totalItems={competitions.length}
            pageSize={competitionPageSize}
            itemUnit="件"
            ariaLabel={`${title}のページ切り替え`}
            onPageChange={onPageChange}
          />
        </>
      )}
    </section>
  );
}

export function ActiveCompetitions({
  year,
  seasonType,
}: {
  year: number;
  seasonType: "fiscal" | "calendar";
}) {
  const [state, setState] = useState<CompetitionsState>("loading");
  const [activeCompetitions, setActiveCompetitions] = useState<
    ActiveCompetition[]
  >([]);
  const [completedCompetitions, setCompletedCompetitions] = useState<
    ActiveCompetition[]
  >([]);
  const [activePage, setActivePage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);

  useEffect(() => {
    let active = true;
    setActivePage(1);
    setCompletedPage(1);

    async function loadCompetitions() {
      try {
        const params = new URLSearchParams({
          year: String(year),
          season_type: seasonType,
        });
        const response = await fetch(
          `/api/league_active_competitions.php?${params.toString()}`
        );
        const result = (await response
          .json()
          .catch(() => ({}))) as CompetitionsResponse;

        if (!response.ok || result.status !== "ok") {
          throw new Error(
            result.message || "コンペ別順位を取得できませんでした。"
          );
        }

        if (active) {
          setActiveCompetitions(
            Array.isArray(result.active_competitions)
              ? result.active_competitions
              : Array.isArray(result.competitions)
              ? result.competitions
              : []
          );
          setCompletedCompetitions(
            Array.isArray(result.completed_competitions)
              ? result.completed_competitions
              : []
          );
          setState("ready");
        }
      } catch {
        if (!active) return;

        if (isLocalDevelopment()) {
          setActiveCompetitions(mockActiveCompetitions);
          setCompletedCompetitions(mockCompletedCompetitions);
          setState("ready");
        } else {
          setState("error");
        }
      }
    }

    void loadCompetitions();
    return () => {
      active = false;
    };
  }, [seasonType, year]);

  return (
    <section
      className="active-competitions"
      aria-labelledby="competition-rankings-title"
    >
      <h2 className="sr-only" id="competition-rankings-title">
        コンペ別順位
      </h2>
      <CompetitionGroup
        kind="active"
        state={state}
        competitions={activeCompetitions}
        currentPage={activePage}
        onPageChange={setActivePage}
      />
      <CompetitionGroup
        kind="completed"
        state={state}
        competitions={completedCompetitions}
        currentPage={completedPage}
        onPageChange={setCompletedPage}
      />
      <p className="active-competitions-note">
        ※順位・参加状況は毎日更新されます。
      </p>
    </section>
  );
}
